import {
  setDeviceMuteStatusAsync,
  toggleNotificationsFromDevicesAsync,
} from "@/base/farmhq-api"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { useToasts } from "@/hooks/useToasts"
import { usePermissionCheckedAction } from "@/hooks/usePermissionCheckedAction"
import * as Models from "@/redux/models"
import type { RootState } from "@/redux/root.reducer"
import { getFarmUserPermissionsFromState } from "@/redux/user-permissions.reducer"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogBodyText,
  AppIcons,
  AppText,
  Button,
  COLORS,
  Pressable,
} from "@/theme"
import { useRouter } from "expo-router"
import React from "react"
import { StyleSheet, View } from "react-native"
import { useTranslation } from "react-i18next"

type NotificationState = "enabled" | "muted-for-me" | "muted-for-all"

const ICON_SIZE = 28
const BUTTON_SIZE = 44

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
  },
  disabled: {
    opacity: 0.4,
  },
  iconButton: {
    alignItems: "center",
    borderColor: COLORS.$white,
    borderRadius: ICON_SIZE / 2,
    borderWidth: 2,
    height: ICON_SIZE,
    justifyContent: "center",
    width: ICON_SIZE,
  },
  pressable: {
    alignItems: "center",
    height: BUTTON_SIZE,
    justifyContent: "center",
    width: BUTTON_SIZE,
  },
})

function getNotificationStateFromRedux(
  state: RootState,
  deviceId: string,
): { hasPhone: boolean; value: NotificationState } {
  const verifiedPhoneNumbers = Models.phoneNumber
    .selectAll(state)
    .filter((pn) => pn.isVerified)

  if (verifiedPhoneNumbers.length === 0) {
    return { hasPhone: false, value: "enabled" }
  }

  const isMuted =
    Models.deviceConfiguration.selectById(state, deviceId)
      ?.notificationsMuted === true

  if (isMuted) {
    return { hasPhone: true, value: "muted-for-all" }
  }

  const isSuppressed =
    Models.deviceUserAssociation.selectById(state, deviceId)
      ?.notificationsSuppressed === true

  if (isSuppressed) {
    return { hasPhone: true, value: "muted-for-me" }
  }

  return { hasPhone: true, value: "enabled" }
}

// eslint-disable-next-line consistent-return
function getIconProps(notificationState: NotificationState) {
  switch (notificationState) {
    case "enabled":
      return {
        IconComponent: AppIcons.NotificationsOn,
        color: COLORS.$success[500],
      }
    case "muted-for-me":
      return {
        IconComponent: AppIcons.NotificationsOff,
        color: COLORS.$error[500],
      }
    case "muted-for-all":
      return {
        IconComponent: AppIcons.NotificationsOff,
        color: COLORS.$gray[400],
      }
  }
}

/**
 * Tappable bell icon that cycles through notification states.
 * Green bell = enabled, red crossed bell = muted for me,
 * grey crossed bell = muted for all.
 *
 * Admin: cycles enabled -> muted-for-me -> muted-for-all -> enabled
 * Non-admin: toggles enabled <-> muted-for-me (disabled if admin-muted)
 */
export function DeviceNotificationStateControl({
  deviceId,
}: Models.DeviceIdProps): React.JSX.Element {
  const { t } = useTranslation("deviceNotifications")
  const router = useRouter()
  const [isDenied, setIsDenied] = React.useState(false)
  const toast = useToasts()

  const { hasPhone, value: notificationState } = useRootSelector((state) =>
    getNotificationStateFromRedux(state, deviceId),
  )

  const canManage = useRootSelector(
    (state) =>
      getFarmUserPermissionsFromState(state)?.canManageDeviceNotifications ===
      true,
  )

  const { dispatch, handleError, isLoading } = useRequestHelpers(
    "SetDeviceMuteStatus",
  )

  const withPermissions = usePermissionCheckedAction()

  const applyState = React.useCallback(
    (nextState: NotificationState) => {
      if (!hasPhone) {
        setIsDenied(true)
        return
      }

      const actions: Array<Promise<unknown>> = []

      if (nextState === "muted-for-me") {
        actions.push(
          dispatch(
            toggleNotificationsFromDevicesAsync({
              action: "SUPPRESS",
              ids: [deviceId],
            }),
          ).unwrap(),
        )
      } else {
        actions.push(
          dispatch(
            toggleNotificationsFromDevicesAsync({
              action: "ENABLE",
              ids: [deviceId],
            }),
          ).unwrap(),
        )
      }

      if (nextState === "muted-for-all") {
        actions.push(
          dispatch(
            setDeviceMuteStatusAsync({ deviceId, muted: true }),
          ).unwrap(),
        )
      } else if (notificationState === "muted-for-all") {
        actions.push(
          dispatch(
            setDeviceMuteStatusAsync({ deviceId, muted: false }),
          ).unwrap(),
        )
      }

      Promise.all(actions)
        .then(() => {
          const messages: Record<NotificationState, string> = {
            "enabled": t("unmutedForMe"),
            "muted-for-all": t("muteForAllUsers"),
            "muted-for-me": t("mutedForMe"),
          }
          toast.success(messages[nextState])
          return undefined
        })
        .catch((error) => handleError(error, { toastMessage: "default" }))
    },
    [
      deviceId,
      dispatch,
      handleError,
      hasPhone,
      notificationState,
      t,
      toast,
    ],
  )

  const cycleState = React.useCallback(() => {
    if (canManage) {
      // Admin cycle: enabled -> muted-for-me -> muted-for-all -> enabled
      const adminCycle: Record<NotificationState, NotificationState> = {
        "enabled": "muted-for-me",
        "muted-for-all": "enabled",
        "muted-for-me": "muted-for-all",
      }
      applyState(adminCycle[notificationState])
    } else {
      // Non-admin: toggle enabled <-> muted-for-me
      if (notificationState === "muted-for-all") {
        return
      }
      applyState(
        notificationState === "enabled" ? "muted-for-me" : "enabled",
      )
    }
  }, [applyState, canManage, notificationState])

  const handlePress = canManage
    ? withPermissions({
        callback: cycleState,
        required: "canManageDeviceNotifications",
      })
    : cycleState

  const { color, IconComponent } = getIconProps(notificationState)
  const isDisabled =
    isLoading || (!canManage && notificationState === "muted-for-all")

  const stateLabels: Record<NotificationState, string> = {
    "enabled": t("stateEnabled"),
    "muted-for-all": t("stateMutedForAll"),
    "muted-for-me": t("stateMutedForMe"),
  }

  return (
    <View style={styles.container}>
      <AlertDialog
        id={"deviceNotificationsToggleDenied"}
        isOpen={isDenied}
        titleElement={t("noVerifiedPhoneNumbersTitle")}
        actionElement={
          <Button
            id={"addPhoneNumberLink"}
            text={t("continue", { ns: "common" })}
            onPress={() => router.navigate("/more/phone-numbers/add")}
          />
        }
        onClose={() => setIsDenied(false)}
      >
        <AlertDialogBody>
          <AlertDialogBodyText>
            {t("noVerifiedPhoneNumbersMessage")}
          </AlertDialogBodyText>
        </AlertDialogBody>
      </AlertDialog>
      <Pressable
        accessibilityLabel={stateLabels[notificationState]}
        disabled={isDisabled}
        id={"deviceNotificationStateControl"}
        style={[styles.pressable, isDisabled ? styles.disabled : undefined]}
        onPress={handlePress}
      >
        <View style={[styles.iconButton, { backgroundColor: color }]}>
          <IconComponent color={COLORS.$white} size={16} />
        </View>
      </Pressable>
      {!canManage && notificationState === "muted-for-all" ? (
        <AppText colorScheme={"secondary"} fontSize={"$xs"}>
          {t("disabledByAdmin")}
        </AppText>
      ) : null}
    </View>
  )
}

// Re-export under old name for backwards compat with SelectedDeviceOptionsMenu
export { DeviceNotificationStateControl as DeviceNotificationsToggleSwitch }
